import React, { useContext, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductContext } from '../../contexts/ProductContext';
import { API_URL } from '../../utils/apiConstant';

const toAbsoluteLogo = (logoUrl: string | null): string | undefined => {
  if (!logoUrl) return undefined;
  return logoUrl.startsWith('http') ? logoUrl : `${API_URL}/uploads/products/${logoUrl}`;
};

// Branding logos are uploaded via the reused product-logo-upload middleware (US-036), a
// different directory than the Product Master's own logo_url — resolved separately.
const toAbsoluteBrandingLogo = (logoUrl: string | null): string | undefined => {
  if (!logoUrl) return undefined;
  return logoUrl.startsWith('http') ? logoUrl : `${API_URL}/uploads/product-logos/${logoUrl}`;
};

const ProductSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const { selectedProductId, setSelectedProduct, accessibleProducts, isGlobalProductScope, activeBranding } =
    useContext(ProductContext);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (accessibleProducts.length === 0) return null;

  // Only global/P1 users may pick "All Products" (AC-2); product-scoped users
  // may only switch between the products in their own scope (AC-3).
  const showAllProductsOption = isGlobalProductScope;
  const selected = accessibleProducts.find((p) => p.id === selectedProductId);
  const canSwitch = accessibleProducts.length > 1 || showAllProductsOption;

  // AC4: once branding resolves for the active product, its display name/logo (product-scope
  // override, or global fallback) takes precedence over the raw Product Master row.
  const displayName = activeBranding?.displayName || selected?.product_name;
  const displayLogoUrl = activeBranding?.logoUrl
    ? toAbsoluteBrandingLogo(activeBranding.logoUrl)
    : toAbsoluteLogo(selected?.logo_url ?? null);

  const handleSelect = (id: number | null) => {
    setSelectedProduct(id);
    setOpen(false);
  };

  return (
    <li ref={ref} className="nav-item navbar-dropdown dropdown product-switcher me-3">
      <a
        className={`nav-link dropdown-toggle hide-arrow ${open ? 'show' : ''}`}
        onClick={() => canSwitch && setOpen((prev) => !prev)}
        style={{ cursor: canSwitch ? 'pointer' : 'default' }}
      >
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0 me-2">
            <div className="avatar avatar-sm">
              {displayLogoUrl ? (
                <img src={displayLogoUrl} alt={displayName ?? ''} className="rounded-circle" />
              ) : (
                <span className="avatar-initial rounded-circle bg-label-secondary">
                  {displayName ? displayName.charAt(0).toUpperCase() : t('productSwitcher.allProducts').charAt(0)}
                </span>
              )}
            </div>
          </div>
          <span className="fw-medium small">
            {displayName ?? t('productSwitcher.allProducts')}
          </span>
        </div>
      </a>
      {canSwitch && (
        <ul className={`dropdown-menu dropdown-menu-end product-switcher-menu ${open ? 'show' : ''}`}>
          {showAllProductsOption && (
            <li>
              <a
                className={`dropdown-item cursor-pointer ${selectedProductId === null ? 'active' : ''}`}
                onClick={() => handleSelect(null)}
              >
                {t('productSwitcher.allProducts')}
              </a>
            </li>
          )}
          {showAllProductsOption && <li><div className="dropdown-divider"></div></li>}
          {accessibleProducts.map((product) => (
            <li key={product.id}>
              <a
                className={`dropdown-item cursor-pointer ${selectedProductId === product.id ? 'active' : ''}`}
                onClick={() => handleSelect(product.id)}
              >
                {product.product_name}
              </a>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export default ProductSwitcher;
